const SUPPORTED_LANGUAGES = ["en", "es", "gl"];
const DEFAULT_LANGUAGE = "en";
const LANGUAGE_KEY = "gatsby-i18next-language";

const isSupportedLanguage = (language) =>
  SUPPORTED_LANGUAGES.includes(language);

const getLanguageFromPath = (pathname) => {
  const firstSegment = pathname.split("/").filter(Boolean)[0];

  return isSupportedLanguage(firstSegment) ? firstSegment : null;
};

const buildPath = (segments, trailingSlash) => {
  const path = `/${segments.join("/")}`.replace(/\/{2,}/g, "/");

  if (path !== "/" && trailingSlash && !path.endsWith("/")) {
    return `${path}/`;
  }

  return path;
};

const getBrowserLanguage = (browserLanguages = []) =>
  browserLanguages
    .map((language) => language && language.toLowerCase().split("-")[0])
    .find(isSupportedLanguage) || DEFAULT_LANGUAGE;

const resolveLanguageRoute = ({
  pathname,
  storedLanguage,
  browserLanguages,
}) => {
  const trailingSlash = pathname.endsWith("/");
  const segments = pathname.split("/").filter(Boolean);
  const explicitLanguage = getLanguageFromPath(pathname);

  if (explicitLanguage) {
    const normalizedSegments = [explicitLanguage];
    let segmentIndex = 1;

    // The first language is authoritative; discard any accidental extra prefixes.
    while (isSupportedLanguage(segments[segmentIndex])) {
      segmentIndex += 1;
    }
    normalizedSegments.push(...segments.slice(segmentIndex));

    return {
      language: explicitLanguage,
      pathname: buildPath(normalizedSegments, trailingSlash),
    };
  }

  const preferredLanguage = isSupportedLanguage(storedLanguage)
    ? storedLanguage
    : getBrowserLanguage(browserLanguages);

  return {
    language: preferredLanguage,
    pathname: buildPath([preferredLanguage, ...segments], trailingSlash),
  };
};

const localizeUrl = (url, language) => {
  if (!isSupportedLanguage(language) || typeof url !== "string") {
    return url;
  }

  const isAbsolute = /^[a-z][a-z\d+.-]*:/i.test(url) || url.startsWith("//");
  if (isAbsolute || !url.startsWith("/")) {
    return url;
  }

  const match = url.match(/^([^?#]*)(.*)$/);
  const pathname = match[1] || "/";
  const suffix = match[2] || "";

  if (getLanguageFromPath(pathname)) {
    return url;
  }

  const segments = pathname.split("/").filter(Boolean);
  const localizedPath = buildPath(
    [language, ...segments],
    pathname === "/" || pathname.endsWith("/"),
  );

  return `${localizedPath}${suffix}`;
};

const removeLanguageFromPath = (pathname) => {
  if (!getLanguageFromPath(pathname)) {
    return pathname;
  }

  const trailingSlash = pathname.endsWith("/");
  const segments = pathname.split("/").filter(Boolean).slice(1);

  return buildPath(segments, trailingSlash);
};

module.exports = {
  DEFAULT_LANGUAGE,
  LANGUAGE_KEY,
  SUPPORTED_LANGUAGES,
  getBrowserLanguage,
  getLanguageFromPath,
  isSupportedLanguage,
  localizeUrl,
  removeLanguageFromPath,
  resolveLanguageRoute,
};
